/**
 * Preiskalkulation Formeln
 *
 * Alle Berechnungen sind transparent und nachvollziehbar.
 */

import type { CalculatorInput, CalculatorResult, CalculatorWarning } from '@/types/price-calculator';
import { PLATFORM_PRESETS, FBA_GERMANY_SURCHARGE, FBA_PEAK_SEASON_SURCHARGE } from './presets';

export function calculatePricing(input: CalculatorInput): CalculatorResult {
  const preset = PLATFORM_PRESETS[input.platformId];
  const warnings: CalculatorWarning[] = [];

  // 1. VK Netto berechnen
  const taxMultiplier = 1 + input.taxRate / 100;
  const sellingPriceNet = input.sellingPrice / taxMultiplier;
  const taxAmount = input.sellingPrice - sellingPriceNet;

  // 2. Plattform-Gebühren
  const commissionRate =
    input.platformId === 'custom' ? input.customCommission : preset.commissionPercent;
  const fixedFee = input.platformId === 'custom' ? input.customFixedFee : preset.fixedFee;

  const commissionAmount = input.sellingPrice * (commissionRate / 100);
  const fixedFeeAmount = fixedFee;
  const paymentFees =
    input.sellingPrice * (preset.paymentFeePercent / 100) + preset.paymentFixedFee;
  const platformFees = commissionAmount + fixedFeeAmount + paymentFees;

  // 3. FBA-Kosten
  let fbaFees = 0;
  let fbaGermanySurcharge = 0;
  let fbaPeakSurcharge = 0;

  if (input.useFBA && preset.hasFBA) {
    fbaFees = preset.fbaBasePrice + input.productWeight * preset.fbaWeightFee + input.fbaStorageCost;
    if (input.fbaGermanyOnly) fbaGermanySurcharge = FBA_GERMANY_SURCHARGE;
    if (input.fbaPeakSeason) fbaPeakSurcharge = FBA_PEAK_SEASON_SURCHARGE;
    fbaFees += fbaGermanySurcharge + fbaPeakSurcharge;
  }

  // 4. Versand & Retouren
  const shippingCosts = Math.max(0, input.shippingCost - input.shippingToCustomer);
  const returnCosts = (input.returnRate / 100) * input.returnCost;

  // 5. Gesamtkosten
  const totalCosts =
    input.purchasePrice +
    platformFees +
    fbaFees +
    shippingCosts +
    input.packagingCost +
    returnCosts +
    input.otherCosts;

  // 6. Erträge
  const grossProfit = sellingPriceNet - input.purchasePrice;
  const netProfit = sellingPriceNet - totalCosts;

  // 7. Kennzahlen
  const marginPercent = input.sellingPrice > 0 ? (netProfit / input.sellingPrice) * 100 : 0;
  const markupPercent =
    input.purchasePrice > 0
      ? ((sellingPriceNet - input.purchasePrice) / input.purchasePrice) * 100
      : 0;

  const capitalEmployed = input.purchasePrice + input.packagingCost + input.otherCosts;
  const roi = capitalEmployed > 0 ? (netProfit / capitalEmployed) * 100 : 0;

  const contributionMargin =
    sellingPriceNet - (input.purchasePrice + input.packagingCost + shippingCosts);
  const contributionMarginPercent =
    sellingPriceNet > 0 ? (contributionMargin / sellingPriceNet) * 100 : 0;

  const effectiveCommissionPercent =
    input.sellingPrice > 0 ? (platformFees / input.sellingPrice) * 100 : 0;

  // 8. Break-even
  const breakEvenPrice =
    commissionRate < 100
      ? (totalCosts * taxMultiplier) / (1 - commissionRate / 100)
      : totalCosts * taxMultiplier;

  const breakEvenUnits =
    capitalEmployed > 0 && netProfit > 0 ? Math.ceil(capitalEmployed / netProfit) : 0;

  // 9. Warnungen
  if (netProfit < 0) {
    warnings.push({ type: 'error', message: `Negativer Gewinn: ${netProfit.toFixed(2)}€` });
  }
  if (marginPercent > 0 && marginPercent < 10) {
    warnings.push({ type: 'warning', message: `Niedrige Marge: ${marginPercent.toFixed(1)}%` });
  }
  if (input.returnRate > 15) {
    warnings.push({ type: 'warning', message: `Hohe Retourenquote: ${input.returnRate}%` });
  }

  return {
    sellingPriceGross: input.sellingPrice,
    sellingPriceNet,
    taxAmount,
    platformFees,
    commissionAmount,
    fixedFeeAmount,
    paymentFees,
    fbaFees,
    fbaGermanySurcharge,
    fbaPeakSurcharge,
    shippingCosts,
    packagingCosts: input.packagingCost,
    returnCosts,
    totalCosts,
    grossProfit,
    netProfit,
    marginPercent,
    markupPercent,
    roi,
    contributionMargin,
    contributionMarginPercent,
    breakEvenPrice,
    breakEvenUnits,
    effectiveCommissionPercent,
    warnings,
  };
}
